import type { ResultOf } from '@graphql-typed-document-node/core'
import { useAtom, useAtomValue } from 'jotai'
import { useCallback, useEffect, useRef } from 'react'
import type { MouseEvent } from 'react'
import type { CourseSelectionInfo } from '~/components/course/CourseSelectionInfo.frag'
import type { PlanSelectionInfo } from '~/components/plan/PlanSelectionInfo.frag'
import type { PlanId, RestaurantId } from '~/lib/graphql/scalars'
import type { MealPeriodTab } from '~/lib/mealPeriod/tab'
import {
  type CourseModalContext,
  useReserveNavigate,
  useReserveNavigationState,
  useReserveOverlayContext,
} from '~/navigation'
import {
  courses$,
  setCriteria$,
  setVariables$,
  useHasSearchInput,
  useSeatOnlyPlans,
  variables$,
} from './courseList'

export function useCourseSection() {
  const context = useReserveOverlayContext('GUIDE')
  const [, setVariables] = useAtom(setVariables$)
  const [, setCriteria] = useAtom(setCriteria$)
  const variables = useAtomValue(variables$)
  const { data, isPending, error } = useAtomValue(courses$)
  const seatOnlyPlans = useSeatOnlyPlans()
  const hasSearchInput = useHasSearchInput()
  useEffect(() => {
    setVariables(context)
    setCriteria(context)
  }, [context, setCriteria, setVariables])

  return { data, isPending, error, variables, seatOnlyPlans, hasSearchInput }
}

export function useRefTitle() {
  const context = useReserveOverlayContext('GUIDE')
  const refTitle = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    if (context?.focus !== 'planList') {
      return
    }
    function showTitle() {
      if (refTitle.current !== null) {
        refTitle.current.scrollIntoView({ behavior: 'smooth' })
      } else {
        setTimeout(showTitle, 10)
      }
    }
    showTitle()
  }, [context])
  return refTitle
}

export function useSeatOnlyCourseNavigation(
  seatOnlyPlans: ResultOf<typeof PlanSelectionInfo>[],
  mealPeriod?: MealPeriodTab
) {
  const planId = seatOnlyPlans.filter(
    (plan) => mealPeriod === undefined || plan.mealPeriod === mealPeriod
  )[0]?.planId
  const seatOnlyPlanIds = seatOnlyPlans.map((plan) => plan.planId)
  const navigate = useReserveNavigate()

  return useCallback(() => {
    navigate({
      type: 'reserve',
      planId,
      seatOnlyPlanIds,
    })
  }, [navigate, planId, seatOnlyPlanIds])
}

export function useCourseMenuNavigation(
  course: ResultOf<typeof CourseSelectionInfo>,
  plans: CourseModalContext['plans'],
  restaurant: CourseModalContext['restaurant']
) {
  const navigate = useReserveNavigate()

  return useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      navigate({
        type: 'openCourseModal',
        restaurant,
        course,
        plans,
      })
    },
    [navigate, restaurant, course, plans]
  )
}

export function usePlanItemNavigation({
  restaurantId,
  planId,
}: { restaurantId: RestaurantId; planId: PlanId }) {
  const navigate = useReserveNavigate()
  const context = useReserveNavigationState('GUIDE', 'COURSE_SELECT')?.context

  const onClickPlan = useCallback(() => {
    navigate({
      type: 'reserve',
      planId: planId,
      seatTypes: context?.seatTypes,
      windowSide: context?.windowSide,
    })
  }, [context, navigate, planId])

  const onClickDetail = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      navigate({
        type: 'openPlanModal',
        restaurantId,
        planId,
      })
    },
    [navigate, restaurantId, planId]
  )

  return { onClickPlan, onClickDetail }
}
